import { useFormSchema } from '@/form/hooks/useFormSchema';
import { isFieldRequired } from '@/form/zod.utils';
import {
  AutoComplete,
  AutoCompleteProps,
  Checkbox,
  CheckboxProps,
  DatePicker,
  DatePickerProps,
  Form,
  FormItemProps,
  Input,
  InputProps,
  Radio,
  RadioGroupProps,
  Select,
  SelectProps,
  Switch,
  SwitchProps,
  TimePicker,
  TimePickerProps,
} from 'antd';
import { CheckboxGroupProps } from 'antd/es/checkbox';
import { RangePickerProps } from 'antd/es/date-picker';
import { FormItemInputProps } from 'antd/es/form/FormItemInput';
import classNames from 'classnames';
import React, { ComponentType, memo, ReactNode, Ref, useMemo } from 'react';
import {
  Control,
  FieldValues,
  useController,
  UseControllerProps,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { formItemPropsKeys } from '../../type';
import './styles.scss';

/* -------------------------------------------------------------------------- */
/*                                  Utilities                                 */
/* -------------------------------------------------------------------------- */

function composeRefs<T>(...refs: (Ref<T> | undefined)[]) {
  return (node: T) => {
    refs.forEach(ref => {
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref != null) {
        (ref as React.MutableRefObject<T>).current = node;
      }
    });
  };
}

/* -------------------------------------------------------------------------- */
/*                                  Types                                     */
/* -------------------------------------------------------------------------- */

export type RHFInputType =
  | 'input'
  | 'select'
  | 'number'
  | 'switch'
  | 'textarea'
  | 'checkbox'
  | 'datePicker'
  | 'timePicker'
  | 'radioGroup'
  | 'rangePicker'
  | 'autoComplete'
  | 'checkboxGroup';

export const controlledInputComponents: Record<
  RHFInputType,
  ComponentType<any>
> = {
  input: Input,
  number: Input,
  select: Select,
  switch: Switch,
  checkbox: Checkbox,
  datePicker: DatePicker,
  timePicker: TimePicker,
  radioGroup: Radio.Group,
  textarea: Input.TextArea,
  autoComplete: AutoComplete,
  checkboxGroup: Checkbox.Group,
  rangePicker: DatePicker.RangePicker,
};

/* -------------------------------------------------------------------------- */
/*                            Main Controlled Input                           */
/* -------------------------------------------------------------------------- */

// #region Main controlled input

export type InputTypePropsMap = {
  input: Omit<InputProps, 'status'> & FormItemInputProps;
  number: Omit<InputProps, 'status'> & FormItemInputProps;
  select: Omit<SelectProps, 'status'> & FormItemInputProps;
  checkbox: Omit<CheckboxProps, 'status'> & FormItemInputProps;
  datePicker: Omit<DatePickerProps, 'status'> & FormItemInputProps;
  timePicker: Omit<TimePickerProps, 'status'> & FormItemInputProps;
  radioGroup: Omit<RadioGroupProps, 'status'> & FormItemInputProps;
  rangePicker: Omit<RangePickerProps, 'status'> & FormItemInputProps;
  autoComplete: Omit<AutoCompleteProps, 'status'> & FormItemInputProps;
  checkboxGroup: Omit<CheckboxGroupProps, 'status'> & FormItemInputProps;
  switch: Omit<SwitchProps, 'status'> & { value?: any } & FormItemInputProps;
};

export type ControlledInputRefType = {
  /** Reference to the input element, preferred for handling refs. */
  inputRef?: Ref<any>;

  /** @deprecated Please use inputRef instead of ref due to forwardRef limitations and creator's skill issue hihi */
  ref?: `Use "inputRef" instead`;
};

type OtherInputProps = {
  [K in Exclude<keyof InputTypePropsMap, 'input' | 'number'>]: {
    inputType: K;
  } & InputTypePropsMap[K];
}[Exclude<keyof InputTypePropsMap, 'input' | 'number' | 'textarea'>];

type DefaultInputProps = {
  inputType?: 'input' | 'number' | 'textarea';
} & InputTypePropsMap['input'];

export type OmitControlControlledInputProps<FV extends FieldValues> = Omit<
  FormItemProps,
  'rules' | 'name'
> &
  UseControllerProps<FV> &
  (DefaultInputProps | OtherInputProps) &
  ControlledInputRefType & { inputClassName?: string };

export type ControlledInputProps<FV extends FieldValues> =
  OmitControlControlledInputProps<FV> & {
    control: Control<FV>;
  };

const BaseControlledInput = <FV extends FieldValues>(
  props: ControlledInputProps<FV>,
) => {
  const {
    name,
    rules,
    value,
    control,
    required,
    onChange,
    className,
    inputType,
    inputClassName,
    inputRef: userInputRef,
    ...rest
  } = props;

  const { t, i18n } = useTranslation();

  const formItemProps = Object.entries(props).reduce(
    (acc, [key, value]) => {
      if (formItemPropsKeys.includes(key)) {
        acc[key] = value;
      }
      return acc;
    },
    {} as Record<string, unknown>,
  );

  const {
    field,
    fieldState: { error },
  } = useController({ name, control, rules });

  const Component = useMemo(
    () => controlledInputComponents[inputType ?? 'input'],
    [inputType],
  );

  const handleChange = (arg0: any, arg1: any) => {
    onChange?.(arg0, arg1);

    const value =
      inputType === 'checkbox'
        ? arg0 &&
          typeof arg0 === 'object' &&
          arg0.target &&
          typeof arg0.target === 'object' &&
          'checked' in arg0.target
          ? arg0.target.checked
          : undefined
        : arg0;

    field.onChange(value ?? null, arg1);
  };

  const handleBlur = (arg0: any) => {
    if (
      inputType !== 'checkboxGroup' &&
      inputType !== 'checkbox' &&
      inputType !== 'switch'
    ) {
      props?.onBlur?.(arg0);
    }
    field.onBlur();
  };

  const formSchema = useFormSchema();

  const zodRequired = isFieldRequired(formSchema, name);

  const requiredRule = !!(rules?.required ?? required ?? zodRequired);

  const componentProps = useMemo(() => {
    const baseProps = {
      ...rest,
      ...field,
      // autocomplete: name,
    };

    if (inputType === 'number') {
      return { ...baseProps, type: 'number' };
    }

    if (inputType === 'checkbox' || inputType === 'switch') {
      return { ...baseProps, checked: field.value };
    }

    return baseProps;
  }, [field, inputType, rest]);

  const errorMessage = i18n.exists(error?.message ?? '')
    ? t(error?.message ?? '')
    : error?.message;

  return (
    <Form.Item
      {...formItemProps}
      // Caller-provided help/validateStatus (e.g. async checks) only show
      // when the schema itself has no error for this field.
      help={errorMessage ?? (formItemProps.help as ReactNode)}
      required={requiredRule}
      validateStatus={
        error
          ? 'error'
          : ((formItemProps.validateStatus as FormItemProps['validateStatus']) ??
            '')
      }
      className={classNames(className, 'rhf-controlled-input')}
    >
      <Component
        {...componentProps}
        onBlur={handleBlur}
        onChange={handleChange}
        className={classNames(inputClassName)}
        ref={composeRefs(userInputRef, field.ref)}
        loading={
          (componentProps as unknown as Record<string, unknown>)?.loading
            ? 1
            : 0
        }
      />
    </Form.Item>
  );
};

// #endregion

const ControlledInput = memo(
  BaseControlledInput,
) as unknown as typeof BaseControlledInput;

/* -------------------------------------------------------------------------- */
/*                             Compound Components                            */
/* -------------------------------------------------------------------------- */

// #region Compound components

type BaseControlledInputProps<FV extends FieldValues> = Omit<
  FormItemProps,
  'rules' | 'name'
> &
  UseControllerProps<FV> & { control: Control<FV> } & ControlledInputRefType;

/* -------------------------------------------------------------------------- */
/*                                    Input                                   */
/* -------------------------------------------------------------------------- */

type InputControlledInputProps<FV extends FieldValues> =
  BaseControlledInputProps<FV> & Omit<InputProps, 'status'>;

BaseControlledInput.Input = function Input<FV extends FieldValues>(
  props: InputControlledInputProps<FV>,
) {
  return <BaseControlledInput {...props} inputType="input" />;
};

ControlledInput.Input = memo(
  BaseControlledInput.Input,
) as typeof BaseControlledInput.Input;

/* -------------------------------------------------------------------------- */
/*                                   Textarea                                   */
/* -------------------------------------------------------------------------- */

type TextareaControlledInputProps<FV extends FieldValues> =
  BaseControlledInputProps<FV> & InputProps;

BaseControlledInput.Textarea = function Textarea<FV extends FieldValues>(
  props: TextareaControlledInputProps<FV>,
) {
  return <BaseControlledInput {...props} inputType="textarea" />;
};

ControlledInput.Textarea = memo(
  BaseControlledInput.Textarea,
) as typeof BaseControlledInput.Textarea;

/* -------------------------------------------------------------------------- */
/*                                   Number                                   */
/* -------------------------------------------------------------------------- */

BaseControlledInput.Number = function Number<FV extends FieldValues>(
  props: InputControlledInputProps<FV>,
) {
  return <BaseControlledInput {...props} inputType="number" />;
};

ControlledInput.Number = memo(
  BaseControlledInput.Number,
) as typeof BaseControlledInput.Number;

/* -------------------------------------------------------------------------- */
/*                                   Select                                   */
/* -------------------------------------------------------------------------- */

type SelectControlledInputProps<FV extends FieldValues> =
  BaseControlledInputProps<FV> & SelectProps;

BaseControlledInput.Select = function Select<FV extends FieldValues>(
  props: SelectControlledInputProps<FV>,
) {
  return <BaseControlledInput {...props} inputType="select" />;
};

ControlledInput.Select = memo(
  BaseControlledInput.Select,
) as typeof BaseControlledInput.Select;

/* -------------------------------------------------------------------------- */
/*                                Auto Complete                               */
/* -------------------------------------------------------------------------- */

type AutoCompleteControlledInputProps<FV extends FieldValues> =
  BaseControlledInputProps<FV> & AutoCompleteProps;

BaseControlledInput.AutoComplete = function AutoComplete<
  FV extends FieldValues,
>(props: AutoCompleteControlledInputProps<FV>) {
  return <BaseControlledInput {...props} inputType="autoComplete" />;
};

ControlledInput.AutoComplete = memo(
  BaseControlledInput.AutoComplete,
) as typeof BaseControlledInput.AutoComplete;

/* -------------------------------------------------------------------------- */
/*                                 Radio Group                                */
/* -------------------------------------------------------------------------- */

type RadioGroupControlledInputProps<FV extends FieldValues> =
  BaseControlledInputProps<FV> & RadioGroupProps;

BaseControlledInput.RadioGroup = function RadioGroup<FV extends FieldValues>(
  props: RadioGroupControlledInputProps<FV>,
) {
  return <BaseControlledInput {...props} inputType="radioGroup" />;
};

ControlledInput.RadioGroup = memo(
  BaseControlledInput.RadioGroup,
) as typeof BaseControlledInput.RadioGroup;

/* -------------------------------------------------------------------------- */
/*                                   Switch                                   */
/* -------------------------------------------------------------------------- */

type SwitchControlledInputProps<FV extends FieldValues> =
  BaseControlledInputProps<FV> & SwitchProps;

BaseControlledInput.Switch = function Switch<FV extends FieldValues>(
  props: SwitchControlledInputProps<FV>,
) {
  return <BaseControlledInput {...props} inputType="switch" />;
};

ControlledInput.Switch = memo(
  BaseControlledInput.Switch,
) as typeof BaseControlledInput.Switch;

/* -------------------------------------------------------------------------- */
/*                                  Checkbox                                  */
/* -------------------------------------------------------------------------- */

type CheckboxControlledInputProps<FV extends FieldValues> =
  BaseControlledInputProps<FV> & CheckboxProps;

BaseControlledInput.Checkbox = function Checkbox<FV extends FieldValues>(
  props: CheckboxControlledInputProps<FV>,
) {
  return <BaseControlledInput {...props} inputType="checkbox" />;
};

ControlledInput.Checkbox = memo(
  BaseControlledInput.Checkbox,
) as typeof BaseControlledInput.Checkbox;

/* -------------------------------------------------------------------------- */
/*                               Checkbox Group                               */
/* -------------------------------------------------------------------------- */

type CheckboxGroupControlledInputProps<FV extends FieldValues> =
  BaseControlledInputProps<FV> & CheckboxGroupProps;

BaseControlledInput.CheckboxGroup = function CheckboxGroup<
  FV extends FieldValues,
>(props: CheckboxGroupControlledInputProps<FV>) {
  return <BaseControlledInput {...props} inputType="checkboxGroup" />;
};

ControlledInput.CheckboxGroup = memo(
  BaseControlledInput.CheckboxGroup,
) as typeof BaseControlledInput.CheckboxGroup;

/* -------------------------------------------------------------------------- */
/*                                 Date Picker                                */
/* -------------------------------------------------------------------------- */

type DatePickerControlledInputProps<FV extends FieldValues> =
  BaseControlledInputProps<FV> & DatePickerProps;

BaseControlledInput.DatePicker = function DatePicker<FV extends FieldValues>(
  props: DatePickerControlledInputProps<FV>,
) {
  return <BaseControlledInput {...props} inputType="datePicker" />;
};

ControlledInput.DatePicker = memo(
  BaseControlledInput.DatePicker,
) as typeof BaseControlledInput.DatePicker;

/* -------------------------------------------------------------------------- */
/*                                 Time Picker                                */
/* -------------------------------------------------------------------------- */

type TimePickerControlledInputProps<FV extends FieldValues> =
  BaseControlledInputProps<FV> & TimePickerProps;

BaseControlledInput.TimePicker = function TimePicker<FV extends FieldValues>(
  props: TimePickerControlledInputProps<FV>,
) {
  return <BaseControlledInput {...props} inputType="timePicker" />;
};

ControlledInput.TimePicker = memo(
  BaseControlledInput.TimePicker,
) as typeof BaseControlledInput.TimePicker;

/* -------------------------------------------------------------------------- */
/*                                Range Picker                                */
/* -------------------------------------------------------------------------- */

type RangePickerControlledInputProps<FV extends FieldValues> =
  BaseControlledInputProps<FV> & RangePickerProps;

BaseControlledInput.RangePicker = function RangePicker<FV extends FieldValues>(
  props: RangePickerControlledInputProps<FV>,
) {
  return <BaseControlledInput {...props} inputType="rangePicker" />;
};

ControlledInput.RangePicker = memo(
  BaseControlledInput.RangePicker,
) as typeof BaseControlledInput.RangePicker;

// #endregion

export default ControlledInput;

/* -------------------------------------------------------------------------- */
/*                                  Examples                                  */
/* -------------------------------------------------------------------------- */

// #region Examples

/* -------------------------------------------------------------------------- */

//       <ControlledInput
//         name="select"
//         control={control}
//         inputType="select"
//         options={[
//           {
//             label: "option 1",
//             value: "option1",
//           },
//         ]}
//       />

/* -------------------------------------------------------------------------- */

//       <ControlledInput.Select
//         name="select"
//         control={control}
//         options={[
//           {
//             label: "option 1",
//             value: "option1",
//           },
//         ]}
//       />
